import React from "react";
import { isNil } from "ramda";
import { styled } from "styled-components";

import type { Theme } from "lib/theme";
import { typeScale } from "lib/themeUtils";

export enum TagType {
  neutral,
  positive,
  negative,
  warn,
}

const getTextColor: (props: { theme: Theme; $type?: TagType; $dark?: boolean }) => string | undefined = ({
  $dark,
  theme,
  $type,
}) => {
  if (isNil($type)) return theme.colours.grey[800];
  return $dark ? theme.colours.white : "rgba(0, 0, 0, 0.6)";
};

const getBgColor: (props: { theme: Theme; $type?: TagType; $dark?: boolean }) => string | undefined = ({
  theme: { colours },
  $type,
  $dark,
}) => {
  if (isNil($type)) return colours.white;
  return (
    isNil($dark)
      ? {
          [TagType.neutral]: colours.grey[350] as string,
          [TagType.positive]: colours.green[100] as string,
          [TagType.negative]: colours.red[100] as string,
          [TagType.warn]: colours.orange[50] as string,
        }
      : {
          [TagType.neutral]: colours.grey[800] as string,
          [TagType.positive]: colours.green[900] as string,
          [TagType.negative]: colours.red[700] as string,
          [TagType.warn]: colours.orange[500] as string,
        }
  )[$type];
};

const TagStyled = styled.div<{ $type?: TagType; $dark?: boolean }>`
  ${typeScale.sm};

  display: inline-block;
  overflow: hidden;
  padding: 0.15rem 0.5rem 0.15rem 0.5rem;
  background-color: ${getBgColor};
  border-radius: 6px;
  border: ${({ $type, theme }) => (isNil($type) ? `solid 1px ${theme.colours.grey[800]}` : "none")};
  color: ${getTextColor};
  font-weight: bold;
  text-overflow: ellipsis;
  white-space: pre;
`;

export type TagProps = React.PropsWithChildren<{ type?: TagType; dark?: boolean }>;

export const Tag: React.FC<TagProps> = ({ type, dark, children, ...rest }) => (
  <TagStyled $type={type} $dark={dark} {...rest}>
    {children}
  </TagStyled>
);
